import { makeAutoObservable, reaction, toJS } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { UpdateEntityStore } from "../../common/store/types";
import { newIdempotencyKey, requestFingerprint } from "../../../common/utils";
import { reportService } from "../services/reportService";
import {
  ReportValuesInit,
  getReportUpdateVariables,
  getReportValues,
  ReportValuesType,
  ReportErrorsType,
  reportDisplayId,
} from "./ReportCommonStore";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { Report } from "../../../api/graphql";
import { resolveCurrentLang } from "../../../common/utils";
import {
  getReportTypeValues,
  ReportTypeValuesType,
  reportTypeDisplayId,
} from "../../reportType/store/ReportTypeCommonStore";
import { reportTypeService } from "../../reportType/services/reportTypeService";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class ReportUpdateStore implements UpdateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  confirmDialogOpened: boolean = false;
  confirmDialogTitle: string = "";
  confirmDialogDescription: string = "";
  confirmDialogCallback: (() => void) | null = null;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: ReportErrorsType = {};
  service = reportService;
  reportInstance: Report | null = null;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda (envío y relectura): la capa del formulario (SavingOverlay)
  // lo bloquea, así nada de lo que se escribiera lo pisa la relectura.
  isSaving: boolean = false;
  // Se guardó, pero el formulario no se pudo poner al día (la relectura falló, o
  // el formulario cambió mientras se guardaba): Guardar queda deshabilitado hasta
  // recargar (OutdatedAfterSaveNotice), así nunca se guarda desde un estado viejo.
  savedButOutdated: boolean = false;
  // En ese estado, lo que se escribe cuenta como cambio pendiente: la foto del
  // formulario cubre los campos; este vigía, también las grillas y los archivos.
  private editedWhileOutdated: boolean = false;
  private stopOutdatedWatch?: () => void;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = ReportValuesInit();
  nameError: string | undefined;
  reportTypeError: string | undefined;
  reportTypeService = reportTypeService;
  reportTypeSearchValue: string = "";
  reportTypeOptions: ReportTypeValuesType[] = [];
  templateError: string | undefined;
  constantsError: string | undefined;
  descriptionError: string | undefined;

  private customValidators: CustomValidator<ReportValuesType>[] = [];
  private _ownPendingChanges = false;
  // La clave de idempotencia del guardado en curso y lo que mandó: un reintento
  // que manda lo mismo (sin cambios desde el intento que falló) la reusa, y el
  // server lo reconoce si ya lo había aplicado; cualquier cambio, o un guardado
  // exitoso, la renueva.
  private saveAttempt: { key: string; request: string } | null = null;

  private idempotencyKeyFor(variables: object): string {
    const request = requestFingerprint(variables);
    if (!this.saveAttempt || this.saveAttempt.request !== request) {
      this.saveAttempt = { key: newIdempotencyKey(), request };
    }
    return this.saveAttempt.key;
  }

  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction?: () => void;
  originalState: any = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Drawer mode flag - when true, close() pops/closes the drawer instead of navigating
  isDrawerMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: { resultId?: string; cancelled?: boolean }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();

    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(v as CustomValidator<ReportValuesType>);
      }
    });
    makeAutoObservable(this, {
      originalState: false,
    });
    this.setupChangeDetection();
  }

  destroy() {
    // Primero, lejos de lo que un proyecto agrega después del bloque de stopReaction.
    this.stopOutdatedWatch?.();
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(validator: CustomValidator<ReportValuesType>) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  *loadReportById(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    this.reportInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (this.reportInstance) {
      // Lo que se ve es lo que hay en el server: el formulario está al día.
      this.becomeCurrent();
      this.entityValues = getReportValues(this.reportInstance, this.langStore);
      this.saveOriginalState();
    }
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  // Guardar deshabilitado: mientras se guarda, y con el formulario guardado pero desactualizado.
  get saveDisabled() {
    return this.isProcessing || this.savedButOutdated;
  }

  /**
   * Vuelve a leer el registro: la acción del aviso de guardado pero desactualizado.
   * Si la lectura sale bien el formulario queda al día y Guardar se vuelve a
   * habilitar; si no, el error queda a la vista y el aviso sigue.
   */
  *reloadReport(): any {
    if (this.isProcessing) return;
    if (this.pendingChanges) {
      // Lo escrito después de quedar desactualizado se descartaría: se pregunta,
      // como al salir.
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.reloadWithoutSaving,
        this.executeReloadAction.bind(this)
      );
      return;
    }
    yield this.executeReloadAction();
  }

  *executeReloadAction(): any {
    yield this.process(() => this.loadReportById(this.entityValues.id));
  }

  /**
   * Queda guardado pero desactualizado. Se toma la foto del formulario: lo editado
   * antes de guardar ya se guardó y no cuenta; lo que se escriba desde ahora cuenta
   * como cambio pendiente, y Cancelar, la navegación y Recargar avisan antes de
   * descartarlo. Para lo que la foto no cubre (las grillas y los archivos), el
   * vigía lo marca.
   */
  private becomeOutdated() {
    this.savedButOutdated = true;
    this.resetPendingChanges();
    this.saveOriginalState();
    this.editedWhileOutdated = false;
    this.stopOutdatedWatch?.();
    this.stopOutdatedWatch = reaction(
      () => ({ ...toJS(this.entityValues), version_: undefined }),
      () => {
        this.editedWhileOutdated = true;
      },
      { equals: (a, b) => _.isEqual(a, b) }
    );
  }

  /** El formulario está al día (se volvió a leer el registro): se sale del estado. */
  private becomeCurrent() {
    this.stopOutdatedWatch?.();
    this.stopOutdatedWatch = undefined;
    this.editedWhileOutdated = false;
    this.savedButOutdated = false;
  }

  get displayId(): string {
    if (this._ownPendingChanges && this.entityValues) {
      return reportDisplayId(this.entityValues as any, this.langStore);
    }
    if (this.reportInstance) {
      return reportDisplayId(this.reportInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.ReportSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "null",
        label: this.rootStore.langStore.groupReportLabel,
        errors: this.getCardErrors("null"),
      },
    ];
  }

  *returnedData(data: any): any {
    const { callerData, returnData } = data;
    if (
      callerData &&
      returnData &&
      !returnData.cancelled &&
      (returnData.entity || returnData.resultId)
    ) {
      // Delegate to subentity UpdateStore if callerData.entityName matches a subentity

      if (callerData?.fieldName === "reportType") {
        if (returnData.entity) {
          const displayId =
            returnData.entity.displayId ||
            reportTypeDisplayId(returnData.entity, this.langStore);
          this.entityValues.showReportType = {
            id: returnData.entity.id,
            displayId,
          };
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.reportTypeOne({ id: returnData.resultId });
          const reportType = result.reportTypeOne;
          this.entityValues.showReportType = {
            displayId: reportType.displayId,
            id: reportType.id,
          };
          this.setReportTypeOptions([
            getReportTypeValues(reportType, this.langStore),
          ]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  showConfirmDialog(title: string, description: string, callback: () => void) {
    this.confirmDialogTitle = title;
    this.confirmDialogDescription = description;
    this.confirmDialogCallback = callback;
    this.confirmDialogOpened = true;
  }

  closeConfirmDialog() {
    this.confirmDialogOpened = false;
    this.confirmDialogCallback = null;
  }

  *executeConfirmedAction() {
    const callback = this.confirmDialogCallback;
    this.closeConfirmDialog();
    if (callback) {
      yield callback();
    }
  }

  getNameValue(): string {
    return this.entityValues.name;
  }

  setNameValue(value: string) {
    this.entityValues.name = value;
  }

  setReportTypeOptions(values: ReportTypeValuesType[]) {
    this.reportTypeOptions = values;
  }

  *loadReportTypeValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("ReportType");
    const allOptions = cache.getOptions("ReportType") as ReportTypeValuesType[];

    const searchValue = this.reportTypeSearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setReportTypeOptions(filtered);
  }

  getReportTypeOptions() {
    return this.reportTypeOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setReportTypeValue(value: string) {
    this.reportTypeSearchValue = value;
    yield this.loadReportTypeValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedReportType(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewReportType(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "reportType"
      );
    } else {
      if (value) {
        this.entityValues.showReportType = value;
        this.entityValues.__reportType = value.displayId;
      }
    }
  }

  getTemplateValue(): string {
    return this.entityValues.template;
  }

  setTemplateValue(value: string) {
    this.entityValues.template = value;
  }

  getConstantsValue(): string {
    return this.entityValues.constants;
  }

  setConstantsValue(value: string) {
    this.entityValues.constants = value;
  }

  getDescriptionValue(): string {
    return this.entityValues.description;
  }

  setDescriptionValue(value: string) {
    this.entityValues.description = value;
  }

  addNewReportType(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore && parentStore !== this) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "Report",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/reportType/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.reportInstance = null;
    this.entityValues = ReportValuesInit();
    this.cardState = { null: { open: true, errors: [] } };
    this.reportTypeSearchValue = "";
    this.reportTypeOptions = [];
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: ReportValuesType): ReportErrorsType {
    const errors: ReportErrorsType = {};
    this.clearErrors();
    values.name = values.name.trim();
    if (values.name === "") {
      errors.name = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.ReportNameLabel,
      };
    }
    this.reportTypeError = undefined;
    if (values.showReportType === null || values.showReportType.id === "") {
      errors.reportType = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.ReportReportTypeLabel,
      };
    }
    values.template = values.template.trim();
    if (values.template === "") {
      errors.template = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.ReportTemplateLabel,
      };
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined,
      "ReportUpdateStore"
    );
  }

  close(returnData?: { resultId?: string; cancelled?: boolean }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.isDrawerMode) {
      // In drawer mode, close or pop the drawer instead of navigating
      if (this.rootStore.drawerViewStack.length > 1) {
        this.rootStore.popDrawerView();
      } else {
        this.rootStore.closeDrawerView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else {
      this.rootStore.router.goBack();
    }
  }

  get pendingChanges(): boolean {
    // Guardado pero desactualizado: cuenta lo escrito desde que se entró en ese
    // estado (la foto, y el vigía para lo que la foto no cubre), no las filas y
    // los archivos que ya se guardaron.
    if (this.savedButOutdated)
      return this._ownPendingChanges || this.editedWhileOutdated;
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      name: this.entityValues.name,
      template: this.entityValues.template,
      constants: this.entityValues.constants,
      description: this.entityValues.description,
      reportType: this.entityValues.showReportType?.id || null,
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  resetPendingChanges() {
    this._ownPendingChanges = false;
  }

  getChangedFields(): string[] {
    if (!this.pendingChanges) return [];

    const changed: string[] = [];

    if (this._ownPendingChanges && this.originalSnapshot) {
      const current = this.computeSnapshot();

      Object.keys(current).forEach((key) => {
        if (!_.isEqual(current[key], this.originalSnapshot![key])) {
          changed.push(key);
        }
      });
    }

    return changed;
  }

  *saveReport(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: el primero
    // cambia la versión del registro, y el segundo fallaría con RECORD_MODIFIED
    // aunque los datos ya estén guardados. Tampoco con el formulario
    // guardado pero desactualizado: no se guarda desde un estado viejo.
    if (this.saveDisabled) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que haga correr la relectura y el
      // cierre como si éste hubiera salido bien.
      this.savedId = null;
      // Mientras se guarda, la capa no deja editar. Si el formulario cambia igual
      // (algún camino programático), lo que se ve ya no es lo que se guardó, y no
      // se concilia nada: queda guardado pero desactualizado, como si la relectura
      // fallara. La versión nueva, que _save toma de la respuesta, no cuenta.
      let changedWhileSaving = false;
      const stopWatching = reaction(
        () => ({ ...toJS(this.entityValues), version_: undefined }),
        () => {
          changedWhileSaving = true;
        },
        { equals: (a, b) => _.isEqual(a, b) }
      );
      // Desde acá hasta que termina (también la relectura), el formulario está
      // bloqueado por su capa. Pase lo que pase (también si el manejo del error
      // tira), el finally de afuera lo libera.
      this.isSaving = true;
      try {
        try {
          this.status = "processing";
          yield this._save();
        } catch (error) {
          this.status = "error";

          if (error instanceof Error) {
            const apiError = getApiError(error, this.langStore);
            this.showErrorDialog(
              apiError.code,
              apiError.title,
              apiError.message
            );
          } else {
            this.showErrorDialog(this.langStore.serverError, undefined);
          }
        } finally {
          stopWatching();
        }

        if (!this.savedId) {
          // Sin guardado exitoso (falló, o no había nada que guardar): Guardar se
          // vuelve a habilitar.
          if (this.isProcessing) this.status = "ok";
        } else {
          try {
            // El formulario queda con el registro del server, no con lo que mandó:
            // las filas nuevas toman su id, los archivos quedan guardados (no
            // "added", que se volverían a mandar) y version_ es el que quedó. Si el
            // formulario sigue abierto (volver atrás no navega, por ejemplo en una
            // pestaña nueva), el próximo guardado parte de ahí. Si no se puede poner
            // al día (cambió mientras se guardaba, o la relectura falla), el guardado
            // ya está hecho, pero lo que se ve no es lo que hay: queda guardado pero
            // desactualizado, con Guardar deshabilitado y el aviso con Recargar, y
            // no se cierra.
            if (changedWhileSaving) {
              this.becomeOutdated();
            } else {
              try {
                yield this.loadReportById(this.savedId);
              } catch (error) {
                console.warn(
                  "[ReportUpdateStore] saved, but reloading the record failed:",
                  error
                );
                this.becomeOutdated();
              }
            }
            if (!this.savedButOutdated) {
              this.resetPendingChanges();
              this.saveOriginalState();
            }
          } finally {
            // Hasta acá sigue guardando (isProcessing): Guardar deshabilitado, y un
            // segundo guardado no corre mientras se relee.
            this.status = "ok";
          }
          if (!this.savedButOutdated)
            this.close({ resultId: this.savedId, cancelled: false });
        }
      } finally {
        // Pase lo que pase (también si el manejo del error tira), el formulario
        // se libera: la capa se cierra.
        this.isSaving = false;
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  *cancelReport(): any {
    if (this.pendingChanges) {
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.exitWithoutSaving,
        this.executeCancelAction.bind(this)
      );
      return;
    }
    yield this.executeCancelAction();
  }

  executeCancelAction(): void {
    this.cancelled = true;
    this._ownPendingChanges = false;

    this.close({ cancelled: true });
  }

  getState(): InitializeParams {
    return {
      id: this.entityValues.id,
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getReportValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = ReportValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
    this.saveOriginalState();
  }

  *_save(): any {
    if (this.reportInstance) {
      const variables = getReportUpdateVariables(this.entityValues);
      this.savedId = null;
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore
      );
      const result = yield api.reportUpdate({
        ...variables,
        idempotencyKey_: this.idempotencyKeyFor(variables),
      });
      this.savedId = result.reportUpdate.id;
      // La versión nueva la da el server: si el formulario sigue abierto (volver
      // atrás no navega, por ejemplo en una pestaña nueva), el próximo guardado
      // la manda y no choca consigo mismo.
      this.entityValues.version_ = result.reportUpdate.version_;
      this.saveAttempt = null;
      this.reportInstance = result.reportUpdate as Report;
    }
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const idToLoad = params?.id || params?.duplicateFromId;

      if (idToLoad) {
        yield this.loadReportById(idToLoad);

        if (params?.duplicateFromId) {
          this.entityValues.id = "";
        }
      }

      if (params?.callbackData?.filter) {
        Object.assign(this.entityValues, params.callbackData.filter);
      }

      if (params?.uiState?.cardState) {
        Object.assign(this.cardState, params.uiState.cardState);
      }

      if (params?.preloadedData) {
        Object.assign(this.entityValues, params.preloadedData);
      }

      yield this.loadReportTypeValues();
      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
